import React from "react";
import styled from "styled-components";
import StatusBadge from "../components/molecules/StatusBadge";
import type { InvoiceUploadDocumentStatus } from "../models/InvoiceUploadDocumentStatus";
import type { InvoiceUploadUnitStatus } from "../models/InvoiceUploadUnitStatus";
import { StarIcon } from "../components/atoms/icons/StarIcon";
import { theme } from "../theme";
import Tooltip from "../components/molecules/Tooltip";
import Small from "../components/atoms/typography/Small";
import dayjs from "dayjs";
import XS from "../components/atoms/typography/XS";
import { useTranslation } from "react-i18next";
import type { InvoiceUploadType } from "../models/InvoiceUploadType";

const StatusContainer = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

export const UploadUnitStatusBadge = ({
  status,
  statusHistory,
  small,
}: {
  status: InvoiceUploadUnitStatus | InvoiceUploadDocumentStatus;
  statusHistory?: Array<{
    setAt: Date;
    status: InvoiceUploadUnitStatus;
  }>;
  small?: boolean;
}) => {
  const { t } = useTranslation();
  let statusBadge: React.ReactNode;

  switch (status) {
    case "COMPLETED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.registered"
          status="checked"
          small={small}
        />
      );
      break;
    case "PROCESSED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.processed"
          status="info"
          small={small}
        />
      );
      break;
    case "REJECTED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.rejected"
          status="failed"
          small={small}
        />
      );
      break;
    case "UNKNOWN":
      statusBadge = (
        <StatusBadge label="random.unknown" status="disabled" small={small} />
      );
      break;
    case "UNPROCESSED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.unprocessed"
          status="disabled"
          small={small}
        />
      );
      break;
    case "NONE":
      return null;
    case "REGISTERED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.registered"
          status="checked"
          small={small}
        />
      );
      break;
    case "INTERPRETATION_FAILED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.interpretationFailed"
          status="failed"
          small={small}
        />
      );
      break;
    case "MERGED":
      statusBadge = (
        <StatusBadge label={status.toLowerCase()} status="info" small={small} />
      );
      break;
    case "MANUALLY_MARKED_AS_HANDLED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.handled"
          status="checked"
          small={small}
        />
      );
      break;
    case "AUTO_PROCESSED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.autoProcessed"
          status="checked"
          small={small}
        />
      );
      break;
    case "PREPROCESSED":
      statusBadge = (
        <StatusBadge
          label="documentHandler.recieved"
          status="info"
          small={small}
        />
      );
      break;
    case "PREPROCESSED_AWAITING_CLIENT_CONNECTION":
      statusBadge = (
        <StatusBadge
          label="documentHandler.recieved"
          status="info"
          small={small}
        />
      );
      break;
    case "SENT_FOR_INTERPRETATION":
      statusBadge = (
        <StatusBadge
          label="documentHandler.sentForInterpretation"
          status="pending"
          small={small}
        />
      );
      break;
    case "SENT_FOR_INTERPRETATION_WITHOUT_TEMPLATE":
      statusBadge = (
        <StatusBadge
          label="documentHandler.tolkien"
          status="other"
          small={small}
        />
      );
      break;
    default:
      statusBadge = (
        <StatusBadge label={status} status="disabled" small={small} />
      );
  }

  const isAutoProcessed = statusHistory?.some(
    (h) => h.status === "AUTO_PROCESSED",
  );

  return statusHistory ? (
    <Tooltip
      content={
        <div>
          <XS color={theme.colors.textMuted} fontweight={600}>
            {t("random.history").toUpperCase()}
          </XS>
          {statusHistory.map((s) => (
            <Small color={theme.colors.white}>
              {s.status} {dayjs(s.setAt).format("YYYY-MM-DD HH:mm:ss")}
            </Small>
          ))}
        </div>
      }
    >
      <StatusContainer>
        {statusBadge}
        {isAutoProcessed && (
          <StarIcon
            color={theme.colors.secondary}
            fill={theme.colors.secondary}
          />
        )}
      </StatusContainer>
    </Tooltip>
  ) : (
    <StatusContainer>
      {statusBadge}
      {isAutoProcessed && <StarIcon color={theme.colors.primary} />}
    </StatusContainer>
  );
};

export const getDocumentUploadSource = (uploadType?: InvoiceUploadType) => {
  switch (uploadType) {
    case "EMAIL":
      return "common.email";
    case "ISSUER":
      return "documentHandler.myPages";
    case "SKYNET_USER":
      return "documentHandler.manualUpload";
    default:
      return "random.unknown";
  }
};
